import { Dialog, DialogActions, DialogContent, DialogContentText, FormControl, NativeSelect, AppBar, Container, Toolbar, Typography, Box, Grid, Button } from '@mui/material';
import { useEffect, useState, ChangeEvent } from 'react';
import { useNavigate } from "react-router-dom";
import TagManager from 'react-gtm-module';
import { FormattedMessage, useIntl } from 'react-intl';
import { Person } from '@mui/icons-material';
import { ActiveShotCoordinate } from './FittingBL';
import * as FittingBL from './FittingBL';
import FittingShotHistory from './FittingShotHistory';
import FittingCanvas from './FittingCanvas';
import FittingCanvasAttackAngle from './FittingCanvasAttackAngle';
import FittingCanvasShaftLean from './FittingCanvasShaftLean';
import GolferMenu from './GolferMenu';
import { HtmlTooltip } from './HtmlTooltip';
import FittingYourGrinds from './FittingYourGrinds';
import FittingYourShots from './FittingYourShots';
import * as GolferHelper from '../library/GolferHelper';
import * as FitterHelper from '../library/FitterHelper';
import * as GrindLookup from '../library/GrindLookup';
import * as TutorialHelper from '../library/TutorialHelper';
import * as GrindTables from '../library/GrindTables';
import * as SimulatorHelper from '../library/SimulatorHelper';
import * as ClubFittingHelper from '../library/ClubFittingHelper';
import Fitter from '../classes/Fitter';
import * as ShotSelection from '../classes/ShotSelection';
import ISimulator from '../classes/ISimulator';
import SimulatorData from '../classes/SimulatorData';
import Golfer, { Loft, ShaftLeanStatus } from '../classes/Golfer';
import * as TrackMan from '../classes/TrackMan';
import setupInstructions from '../images/SetupInstructions.pdf';

/****************************************
WebSocket Hack:
The websocket is a low level browser call and it can't access React's useState() hooks to get the latest state value.
Thus, It always have the original default value that the form opened with and doesn't know the current values.
To get around this, any function called by the WebSocket object will have to use the following template for each state variable:

let newValue = ...  //assign any value
setXXXX(prevValue => { 
    newValue = prevValue;
    return prevValue;
});
.... //use newValue in your code now

To get around these issues, I had the idea to rewrite the state data using promises b/c promises only allow one thread to run at a time 
and the code waits till each thread completes before continuing. After doing this rewrite things seems to be running smoothly.
If any of the code looks like it's over-architected then it's the result of the web socket causing bugs elsewhere in the code and resolving those issues.
****************************************/

export default function Fitting(props: any) {
    const [golfer, setGolfer] = useState(new Golfer());
    const [lastShotCoordinate, setLastShotCoordinate] = useState(new ActiveShotCoordinate(-1, -1));
    const [activeShotCoordinate, setActiveShotCoordinate] = useState(new ActiveShotCoordinate(0, 0));
    const [shotsToPlot, setShotsToPlot] = useState(new Map<string, ShotSelection.ShotToPlot>());
    const [shotsForGrid, setShotsForGrid] = useState([] as ShotSelection.ShotHistory[]);
    const [shotHistory, setShotHistory] = useState([] as ShotSelection.ShotHistory[]);
    const [showNumberShotsGroup, setShowNumberShotsGroup] = useState('hideShots');
    const [fitter, setFitter] = useState({} as Fitter);
    const [TMMessage, setTMMessage] = useState('');
    const [TMYards, setTMYards] = useState('');
    const [ws, setWs] = useState({} as WebSocket);
    const [tmLastPing, setTmLastPing] = useState(new Date);
    const [waitingForNextShot, setWaitingForNextShot] = useState(false);
    const [sampleShotCounter, setSampleShotCounter] = useState(0);
    const [grindRecommendations, setGrindRecommendations] = useState([] as GrindLookup.LoftGrindRecommendation[]);
    const [showNoClubMeasurement, setShowNoClubMeasurement] = useState(false);
    const [grindDialogOpen, setGrindDialogOpen] = useState(false);
    const [rangeDialogOpen, setRangeDialogOpen] = useState(false);
    const [currentShotType, setCurrentShotType] = useState('');  //used for tracking when the user is moving to a new loft/grind to update the form alerts
    const [trackManData, setTrackManData] = useState({} as TrackMan.Data);
    const [anchorEl, setAnchorEl] = useState(null);
    const [testMode, setTestMode] = useState(false);  //used for debuggging (ethernet address is '*test*')
    const [showTutorial, setShowTutorial] = useState(false);
    const [tutorialId, setTutorialId] = useState(0);
    const [showBadHttpsSettings, setShowBadHttpsSettings] = useState(false);
    const [checkTMConnection, setCheckTMConnection] = useState(false);  //always ping the TM connection unless there is an HTTPS error in which the browser can't even talk to the TM
    const [simulator, setSimulator] = useState({} as ISimulator);
    const [simulatorData, setSimulatorData] = useState(new SimulatorData());
    const [showConsole, setShowConsole] = useState(0);
    const [consoleLog, setConsoleLog] = useState(new Array<String>());

    const intl = useIntl();
    const intlNotConnected = intl.formatMessage({ id: "now.hitting.not.connected" });
    const intlAttemptConnection = intl.formatMessage({ id: "now.hitting.attempt.connection" });
    const intlAllShots = intl.formatMessage({ id: "now.hitting.all.shots" });
    const intlLastShot = intl.formatMessage({ id: "now.hitting.last.shot" });
    const intlHideShots = intl.formatMessage({ id: "now.hitting.hide.shots" });

    const grindItemName = GrindTables.showSM11(fitter.showSm11) ? "SM11" : "SM9";

    //get shot data from iPad
    const iOSMessage = async (msg: string) => {
        consoleLogWrite("iOSMessage");
        consoleLogWrite(msg);
        let simulatorData = JSON.parse(msg) as TrackMan.Data;
        if (simulatorData.Type === "Status") {
            //update the Trackman connection status
            if (simulatorData.SubType === "disconnected") {
                setTMMessage(intlNotConnected);
                setTMYards("");
                (window as any).webkit.messageHandlers.iOSConnectTM.postMessage(true);
            } else {
                setTmLastPing(new Date());  //timestamp of communication
                await updateStatusMessage();  //async to clear any processes before running it
            }
        } else {
            //parse the TrackMan shot data
            let newSampleShotCounter = await sampleShotCounterAsync();
            setSampleShotCounter(newSampleShotCounter + 1);
            let data = TrackMan.convertDataToSimulatorData(simulatorData);
            handleShotEvent(data, msg);
        }
    }

    //convert the shot type enum to international text
    const intlShotName = (name: string) => {
        let intlName = "";
        if (name !== "") {
            let nameArray = name.split(' ');
            if (nameArray.length > 1) {
                intlName = nameArray[0] + ' ' + intl.formatMessage({ id: "shot.selection." + nameArray[1].replace(" ", "").toLowerCase() });
            }
        }
        return intlName;
    }

    const [showGapping, setShowGapping] = useState(false);
    const [shaftLeanStatus, setShaftLeanStatus] = useState("unknown" as ShaftLeanStatus);
    //this tracks when the FS Bridge software reports that the actual device is offline. This will be combined with logic if we are connected to the middleware or not to determine what to show the user
    const [foresightDeviceConnected, setForesightDeviceConnected] = useState(true);

    useEffect((): void => {
        TagManager.dataLayer({
            dataLayer: {
                event: 'vwf_now_hitting',
            }
        });

        // resetTimeout();
        const getAsyncData = async () => {
            //get simulator object
            let newSimulator = await SimulatorHelper.getSimulator();
            setSimulator(newSimulator);
            let simulatorConfiguration = await SimulatorHelper.getConfiguration();
            setShowGapping(simulatorConfiguration.modelType === "TrackMan");
            //tutorila flag
            setShowTutorial(await TutorialHelper.isActive());
            //get trackman defaults, fitter and golfer profiles
            if (newSimulator.getUrl(simulatorConfiguration) === "") {
                setTestMode(true);
            }
            let newFitter = await FitterHelper.getFitter();
            setFitter(newFitter);
            //if iOS device, send message to connect to TM
            if (newFitter.iOS) {
                try {
                    consoleLogWrite("newFitter iOS");
                    //send msg to iOS to connect to TM
                    (window as any).webkit.messageHandlers.iOSRequestStatus.postMessage(true);
                    // iOSLoginTM(); //only needed when connecting to TM standalone
                    // //give the TM a couple seconds to Login before connection to the websocket
                    // setTimeout(() => {
                    //     (window as any).webkit.messageHandlers.iOSConnectTM.postMessage(true);
                    // }, 2000)
                }
                catch {
                    //this errors out when I'm debugging locally. so just ignore it
                }
            }
            let newGolfer = await GolferHelper.getCurrentGolfer();
            //create the list of shots the golfer should take and assign the number of 'slots' to hold the data for each practice shot
            //if they are toggling between pages, then update the list in case they went back and picked different lofts to test
            let loftsForFitting = GolferHelper.calculateLoftsForFitting(newFitter, newGolfer.selectedLofts, newGolfer.loftsForFitting);
            newGolfer.loftsForFitting = loftsForFitting;
            //plot existing shots
            //show the shaft lean if the practice shots have a dynamic loft value
            setShaftLeanStatus(FittingBL.getShaftLeanStatus(simulatorConfiguration.modelType, loftsForFitting));
            let newShotsToPlot = FittingBL.calculateShotsToPlot(simulatorConfiguration.modelType, loftsForFitting, newFitter, newSimulator);
            setShotsToPlot(newShotsToPlot);
            // fill and display the shot history list in bottom grid
            let newShotHistory = FittingBL.populateShotHistoryFromGolfer(newGolfer, newFitter, newSimulator, intl);
            setShotHistory(newShotHistory);
            setShotsForGrid(newShotHistory);
            //clear out previous active shots if is user toggling between pages and then find the next active shot in the grid
            FittingBL.resetActiveShotFlags(newGolfer);
            FittingBL.calculateNextActiveShot(activeShotCoordinate, newGolfer);

            setTMMessage(FittingBL.getNowHittingTitle(activeShotCoordinate, newGolfer, intl));
            setTMYards(FittingBL.getNowHittingYards(activeShotCoordinate, newGolfer, newFitter, intl));

            //compute the grinds recommendations in the sidebar
            let grinds = GrindLookup.getGrindsWithBunkerConfidence(newGolfer, newFitter, newSimulator);

            let newGrindRecommendations = GrindLookup.formatGrindsForOutput(grinds, newGolfer, newSimulator, intl);
            setGrindRecommendations(newGrindRecommendations);

            //save state
            setGolfer(newGolfer);
            if (!testMode && !newFitter.iOS) {
                connectToTrackMan();
            } else {
                updateStatusMessage();
            }
            if (newFitter.iOS) {
                (window as any)["iOSMessage"] = iOSMessage;
            }
        }

        getAsyncData();

    }, []);

    const wsClose = () => {
        try {
            ws.close();
        }
        catch { }
    }

    const cbGolferUpdate = async (cbGolfer: Golfer) => {
        let newGolfer = await golferAsync();
        newGolfer = { ...newGolfer, firstName: cbGolfer.firstName, lastName: cbGolfer.lastName, fullName: cbGolfer.fullName, email: cbGolfer.email, phone: cbGolfer.phone, smsOptIn: cbGolfer.smsOptIn };
        setGolfer(newGolfer);
        await GolferHelper.upsertGolfer(newGolfer);
    }

    //timer event to check the status of the Foresight connection
    useEffect(() => {
        const asyncProcess = async () => {
            let simulatorConfiguration = await SimulatorHelper.getConfiguration();
            if (simulatorConfiguration.modelType === "Foresight") {
                const timer = setTimeout(async () => {
                    let newWs = await wsAsync();
                    if (newWs.readyState === WebSocket.OPEN) {
                        setTmLastPing(new Date());  //timestamp of communication from Foresight
                        updateStatusMessage();
                    } else {
                        if (!showTutorial) {
                            setTMMessage(intlNotConnected);
                            setTMYards("");
                        }
                        connectToTrackMan();
                    }
                    asyncProcess();
                }, 5000)
                return () => {
                    clearTimeout(timer);
                }
            }
        }
        asyncProcess();
    }, []);

    //timer event to check the status of the TrackMan websocket connection. If it is disconnected then attempt to re-connect
    useEffect(() => {
        const asyncProcess = async () => {
            //if hosted in iOS app, then don't connect to TM b/c the iOS app manages that
            let newFitter = await fitterAsync();
            if (!newFitter.iOS) {
                let simulatorConfiguration = await SimulatorHelper.getConfiguration();
                if (simulatorConfiguration.modelType === "TrackMan") {
                    const timer = setTimeout(async () => {
                        let newCheckTMConnection = await checkTMConnectionAsync();
                        if (newCheckTMConnection) {
                            updateStatusMessage();

                            //check TM status
                            let newWs = await wsAsync();
                            if (newWs.readyState != WebSocket.OPEN) {
                                //try to re-establish connecttion
                                if (!showTutorial) {
                                    setTMMessage(intlNotConnected);
                                    setTMYards("");
                                }

                                connectToTrackMan();
                            }
                        }
                        asyncProcess();
                    }, 2000);

                    return () => {
                        clearTimeout(timer);
                    }
                }
            }
        }

        asyncProcess();
    }, []);

    const updateStatusMessage = async () => {
        let newActiveShotCoordinate = await activeShotCoordinateAsync();
        let newFitter = await fitterAsync();

        if (testMode) {
            let newGolfer = await golferAsync();
            let message = FittingBL.getNowHittingTitle(newActiveShotCoordinate, newGolfer, intl);
            setTMMessage(message);
            setTMYards(FittingBL.getNowHittingYards(newActiveShotCoordinate, newGolfer, newFitter, intl));
            return;
        }

        let newTmLastPing = await tmLastPingAsync();
        let time = Math.abs(((new Date()).getTime() - newTmLastPing.getTime()) / 1000);
        //show not connected if the Foresight middleware says the device is offline, or if the last successful ping was over ten seconds.    
        let newForesightDeviceConnected = await foresightDeviceConnectedAsync();

        if (!newForesightDeviceConnected || time > 10) {
            if (!showTutorial) {
                setTMMessage(intlNotConnected);
                setTMYards('');
            }
        } else {
            let newGolfer = await golferAsync();
            setTMMessage(FittingBL.getNowHittingTitle(newActiveShotCoordinate, newGolfer, intl));
            setTMYards(FittingBL.getNowHittingYards(newActiveShotCoordinate, newGolfer, newFitter, intl));
        }
    }

    const connectToTrackMan = async () => {

        let simulatorConfiguration = await SimulatorHelper.getConfiguration();
        let newSimulator = await simulatorAsync();
        let newFitter = await fitterAsync();
        let url = newSimulator.getUrl(simulatorConfiguration);  //refactor: Don't need this test b/c this is covered earlier using testMode flag. But too risky to refactor till we can do a full suite of testing again with Greg 
        if (url != "") {
            try {
                const socket = new WebSocket(url);
                setWs(socket);

                socket.onopen = function (e) {
                    updateStatusMessage();
                    if (simulatorConfiguration.modelType === "TrackMan") {
                        // let subscribe = {
                        //     "Type": "Subscribe",
                        //     "Payload": {
                        //         "MessageList": ["ALL"
                        //         ]
                        //     }
                        // }
                        let subscribe = {
                            "Type": "Subscribe",
                            "Payload": {
                                "MessageList": ["Measurement"
                                ]
                            }
                        }
                        socket.send(JSON.stringify(subscribe));
                        //now that we are connected, ping the socket to make sure we stay connected
                        setCheckTMConnection(true);
                    }
                };

                socket.onmessage = async function (event) {
                    setTmLastPing(new Date());  //timestamp of communication from TrackMan
                    // let newWaitingForNextShot = await waitingForNextShotAsync();
                    //TrackMan requires ping/pong status updates
                    if (event.data.toLowerCase().indexOf("status") > -1) {
                        let isConnected = event.data.toLowerCase() === "status: connected";
                        setForesightDeviceConnected(isConnected);
                    } else {
                        var data = JSON.parse(event.data);

                        if (data.Type && data.Type === "Ping") {
                            //send confirmation back to TM
                            data.Type = "Pong";
                            socket.send(JSON.stringify(data));
                        } else {
                            //convert the data from the specific simulator to the generic data object
                            let newSimulator = await simulatorAsync();
                            if (newSimulator.isValidShotPayload(data)) {
                                let newSimulatorData = newSimulator.handlePayload(data);
                                //process the shot data
                                setSimulatorData(newSimulatorData);
                                handleShotEvent(newSimulatorData, event.data);
                            } else {
                            }
                        }
                    }
                }
                socket.onerror = function (error) {
                    setCheckTMConnection(true);  //continue checking TM waiting for a new connection
                    setTMMessage(intlNotConnected);
                };
            }
            catch (err) {
                TagManager.dataLayer({
                    dataLayer: {
                        event: 'log_error',
                        description: 'Connect to TrackMan',
                        msg: 'catch: ' + err
                    }
                });
                if (err.message.indexOf('insecure WebSocket') > -1) {
                    setCheckTMConnection(false);  //can't talk to websocket, so stop trying to connect
                    setShowBadHttpsSettings(true);
                } else {
                    setTMMessage(intlNotConnected);
                }
            }
        }
    }

    const handleShotEvent = (data: SimulatorData, msg: string) => {
        setSimulatorData(data);
        setWaitingForNextShot(true);
        setShowNoClubMeasurement(false); //immediately disable the 'no shot data' message when the next shot has been detected
        let spinnerDelay = testMode ? 500 : 2000;
        setTimeout(() => {
            setWaitingForNextShot(false);
            processShotEvent(msg);
        }, spinnerDelay);
    }

    //after the spinner completes, take the message and process it/show it on the page
    const processShotEvent = async (msg: string) => {
        //GA event
        TagManager.dataLayer({
            dataLayer: {
                event: 'vwf_shot_made',
                shot_count: golfer.enhancedFittingData.length + 1
            }
        });

        consoleLogWrite('Process Shot Event');
        consoleLogWrite(msg);
        let newGolfer = await golferAsync();
        let newFitter = await fitterAsync();
        if (newGolfer.enhancedFittingData.length === 0) {
            TagManager.dataLayer({
                dataLayer: {
                    event: 'vwf_shot_started',
                }
            });
        }
        // resetTimeout();
        //do unit conversions to raw data
        let newSimulatorData = await simulatorDataAsync();
        let newSimulator = await simulatorAsync();

        //ignore TM practice shots if golfer hasn't said they are ready yet
        // let newWaitingForNextShot = await waitingForNextShotAsync();
        if ((newSimulatorData.attackAngle === undefined && newSimulatorData.dynamicLoft === undefined)) {
            setShowNoClubMeasurement(true);
        } else {
            setShowNoClubMeasurement(false);
        }

        //alert user if shooting outside recommended range
        let newActiveShotCoordinate = await activeShotCoordinateAsync();
        let distance = Math.round(newSimulatorData.carryDistance);
        let shotType = newGolfer.loftsForFitting[newActiveShotCoordinate.row].shotType;
        let showRangeDialog = false;
        if (newSimulator.modelType !== "foresight" || distance > 0) {
            if (
                (shotType === "Pitch" && (distance < 4.57 || distance > 32)) ||
                (shotType === "Open Face" && distance > 27)
            ) {
                setRangeDialogOpen(true);
                setTimeout(() => {
                    setRangeDialogOpen(false);
                }, 4000);
            }
        }

        //update distance units based on fitter preferences
        FittingBL.convertUnits(newSimulatorData, newSimulator, newFitter);

        //just to be safe, don't record the same shot twice. TM sends lots of shot data with the same shot ID.
        //however, Foresight only sends one Id at a time so it isn't relevant to do this for Foresight. And it might actually hurt b/c if 
        //the device is turned on/off then it will reset the Id back to "1" even though it's a unique shot. So that would mess things up.
        let simulatorConfiguration = await SimulatorHelper.getConfiguration();
        if ((simulatorConfiguration.modelType === "Foresight") || FittingBL.shotIdIsUnique(newSimulatorData.Id.toString(), newGolfer)) {
            if (newActiveShotCoordinate.row > -1 && newActiveShotCoordinate.col > -1) {
                if (newGolfer.loftsForFitting[newActiveShotCoordinate.row].practiceShots[newActiveShotCoordinate.col].status === 'active') {
                    newGolfer.loftsForFitting[newActiveShotCoordinate.row].practiceShots[newActiveShotCoordinate.col].status = 'closed';
                    newGolfer.loftsForFitting[newActiveShotCoordinate.row].practiceShots[newActiveShotCoordinate.col].data = newSimulatorData;

                    //determine if the shots have a dynamic loft or not so that the canvas knows how to mark the y-axis (shaft lean)
                    setShaftLeanStatus(FittingBL.getShaftLeanStatus(simulatorConfiguration.modelType, newGolfer.loftsForFitting));

                    await addShotToHistory(newGolfer.loftsForFitting[newActiveShotCoordinate.row].loft, newGolfer.loftsForFitting[newActiveShotCoordinate.row].shotType, newSimulatorData, newFitter, newSimulator);

                    //compute the grinds recommendations for the sidebar
                    let grinds = GrindLookup.getGrindsWithBunkerConfidence(newGolfer, newFitter, newSimulator);
                    let newGrindRecommendations = GrindLookup.formatGrindsForOutput(grinds, newGolfer, newSimulator, intl);
                    setGrindRecommendations(newGrindRecommendations);

                    //push the shot to the enhanced fitting data collection
                    let enhancedData = await GolferHelper.createEnhancedFittingData(newGolfer.loftsForFitting[newActiveShotCoordinate.row].loft, newGolfer.loftsForFitting[newActiveShotCoordinate.row].shotType, newSimulatorData, newFitter, newSimulator, newGrindRecommendations, msg);
                    newGolfer.enhancedFittingData.push(enhancedData);
                    setGolfer(newGolfer);

                    //save the 'delete last shot' coordinate and determine the next shot to take
                    setLastShotCoordinate({ ...newActiveShotCoordinate });
                    let lastShotType = "";
                    if (newActiveShotCoordinate.row !== -1) {
                        lastShotType = newGolfer.loftsForFitting[newActiveShotCoordinate.row].loft + ' ' + newGolfer.loftsForFitting[newActiveShotCoordinate.row].shotType;
                    }
                    //get next active shot coordinate and update the message displayed at top of form
                    FittingBL.calculateNextActiveShot(newActiveShotCoordinate, newGolfer);
                    setTMMessage(FittingBL.getNowHittingTitle(newActiveShotCoordinate, newGolfer, intl));
                    setTMYards(FittingBL.getNowHittingYards(newActiveShotCoordinate, newGolfer, newFitter, intl));
                    setActiveShotCoordinate(newActiveShotCoordinate);  //save

                    if (newActiveShotCoordinate.row > -1 && newActiveShotCoordinate.col > -1) {
                        let nextShotType = newGolfer.loftsForFitting[newActiveShotCoordinate.row].loft + ' ' + newGolfer.loftsForFitting[newActiveShotCoordinate.row].shotType;

                        //show the "change grind dialog" when swtiching to new club/grind
                        if (lastShotType !== "" && lastShotType !== nextShotType) {
                            setCurrentShotType(nextShotType);
                            //do a short delay so the user has time to review the last shot before seeing the dialog appear
                            setTimeout(() => {
                                setGrindDialogOpen(true);
                                setTimeout(() => {
                                    setGrindDialogOpen(false);
                                }, 4000);
                            }, 3000);
                        }
                    }

                    setGolfer(newGolfer);
                    GolferHelper.upsertGolfer(newGolfer);

                    //update the collection for plotting shots on the canvas
                    let newShotsToPlot = FittingBL.calculateShotsToPlot(simulatorConfiguration.modelType, newGolfer.loftsForFitting, newFitter, newSimulator);
                    setShotsToPlot(newShotsToPlot);
                }
            }
        }
    }

    const handleGrindDialogClose = () => {
        setCurrentShotType('');
        setGrindDialogOpen(false);
    }

    const handleRangeDialogClose = () => {
        setRangeDialogOpen(false);
    }

    //when user clicks a new row, remove the current shot selected and move it to the new row
    const handleSelectNewShotRow = async (newShotRow: number) => {
        let newGolfer = await golferAsync();
        let newFitter = await fitterAsync();

        //reset the current 'active' shot back to 'open' unless were already finished with the fitting
        var newActiveShotCoordinate = await activeShotCoordinateAsync();
        if (newActiveShotCoordinate.row !== -1) {
            newGolfer.loftsForFitting[newActiveShotCoordinate.row].practiceShots[newActiveShotCoordinate.col].status = 'open';
        }
        //set the next active shot for the current row clicked on
        newActiveShotCoordinate = new ActiveShotCoordinate(newShotRow, -1);
        //was an existing slot found for this row, or was a new shot added to the end of the row?
        let existingShotFound = FittingBL.setActiveShotForCurrentRow(newActiveShotCoordinate, newGolfer);
        if (!existingShotFound) {
            TagManager.dataLayer({
                dataLayer: {
                    event: 'vwf_shot_added'
                }
            })
        }
        // //save state
        let newTitle = FittingBL.getNowHittingTitle(newActiveShotCoordinate, newGolfer, intl);
        setTMMessage(newTitle);
        setTMYards(FittingBL.getNowHittingYards(newActiveShotCoordinate, newGolfer, newFitter, intl));
        setGolfer(newGolfer);
        GolferHelper.upsertGolfer(newGolfer);
        setActiveShotCoordinate(newActiveShotCoordinate);
    }


    const deleteShotCallBack = async (id: string) => {
        TagManager.dataLayer({
            dataLayer: {
                event: 'vwf_fit_shot_deleted'
            }
        })

        //delete the shot from the practice shots collection
        let newGolfer = await golferAsync();
        let newFitter = await fitterAsync();
        let newSimulator = await simulatorAsync();
        let simulatorConfiguration = await SimulatorHelper.getConfiguration();

        //flag shot as deleted in enhancedFittingData
        newGolfer.enhancedFittingData = ClubFittingHelper.setIsDeleted(id, newGolfer.enhancedFittingData);

        //remove shot from practice shot collection
        FittingBL.deleteShotById(id, newGolfer);
        deleteShotHistoryRecord(id);

        //update shot canvas
        let newShotsToPlot = FittingBL.calculateShotsToPlot(simulatorConfiguration.modelType, newGolfer.loftsForFitting, newFitter, newSimulator);
        setShotsToPlot(newShotsToPlot);

        //compute the grinds recommendations in the sidebar
        let grinds = GrindLookup.getGrindsWithBunkerConfidence(newGolfer, newFitter, newSimulator);
        let newGrindRecommendations = GrindLookup.formatGrindsForOutput(grinds, newGolfer, newSimulator, intl);
        setGrindRecommendations(newGrindRecommendations);

        setGolfer(newGolfer);
        GolferHelper.upsertGolfer(newGolfer);

    }

    let navigate = useNavigate();

    const handleSubmit = async () => {
        var items = grindRecommendations.map(grind => {
            return {
                item_brand: 'Vokey',
                item_name: grindItemName,
                item_variant: `recommended - ${(grind.selectedBounce === grind.grindWeightings[0].bounce).toString()}`,
                quantity: 1,
                item_category: golfer.hand + " Hand",
                item_id: `${grind.loft}.${grind.selectedBounce} ${grind.selectedGrind} Grind`
            }
        })

        TagManager.dataLayer({
            dataLayer: {
                event: 'vwf_now_hitting_why_these_grinds',
                items: items
            }
        })

        //if iOS app, turn off TM connection
        let newFitter = await fitterAsync();
        if (newFitter.iOS) {
            consoleLogWrite("handle Submit");
            (window as any).webkit.messageHandlers.iOSConnectTM.postMessage(false);
        }
        wsClose();
        navigate('/apps/wedgefitting/whygrinds')
    }

    const handleBack = () => {
        wsClose();
        navigate('/apps/wedgefitting/rankshots');
    }

    const handleDeleteLastShot = async () => {
        TagManager.dataLayer({
            dataLayer: {
                event: 'vwf_shot_deleted'
            }
        })

        let newLastShotCoordinate = await lastShotCoordinateAsync();
        let newGolfer = await golferAsync();
        let newFitter = await fitterAsync();
        let newSimulator = await simulatorAsync();

        //flag shot as deleted in enhancedFittingData and remove from history grid
        let shotId = newGolfer.loftsForFitting[newLastShotCoordinate.row].practiceShots[newLastShotCoordinate.col].data!.Id;
        newGolfer.enhancedFittingData = ClubFittingHelper.setIsDeleted(shotId, newGolfer.enhancedFittingData);
        deleteShotHistoryRecord(shotId);

        //only allow one 'redo' so reset to pointer to -1. 
        setLastShotCoordinate({ row: -1, col: -1 });

        //if all shots have not been taken, then set the current shot from active to open;
        if (activeShotCoordinate.row !== -1) {
            newGolfer.loftsForFitting[activeShotCoordinate.row].practiceShots[activeShotCoordinate.col].status = 'open';
        }
        //mark the previous shot as the currently active shot
        newGolfer.loftsForFitting[newLastShotCoordinate.row].practiceShots[newLastShotCoordinate.col].status = 'active';
        newGolfer.loftsForFitting[newLastShotCoordinate.row].practiceShots[newLastShotCoordinate.col].data = undefined;

        //update the Now Hitting message using the previous shot coordinate
        setTMMessage(FittingBL.getNowHittingTitle(newLastShotCoordinate, newGolfer, intl));
        setTMYards(FittingBL.getNowHittingYards(newLastShotCoordinate, newGolfer, newFitter, intl));

        //reset the active shot coordinates to the previous shot
        setActiveShotCoordinate({ ...newLastShotCoordinate });
        setGolfer(newGolfer);
        GolferHelper.upsertGolfer(newGolfer);

        //update shot canvas
        let simulatorConfiguration = await SimulatorHelper.getConfiguration();
        let newShotsToPlot = FittingBL.calculateShotsToPlot(simulatorConfiguration.modelType, newGolfer.loftsForFitting, newFitter, newSimulator);
        setShotsToPlot(newShotsToPlot);

        //compute the grinds recommendations in the sidebar
        let grinds = GrindLookup.getGrindsWithBunkerConfidence(newGolfer, newFitter, newSimulator);
        let newGrindRecommendations = GrindLookup.formatGrindsForOutput(grinds, newGolfer, newSimulator, intl);
        setGrindRecommendations(newGrindRecommendations);
    }

    const foresightDeviceConnectedAsync = () => {
        return new Promise<boolean>((resolve, reject) => {
            setForesightDeviceConnected(prevValue => {
                resolve(prevValue);
                return prevValue;
            })
        })
    }

    const activeShotCoordinateAsync = () => {
        return new Promise<ActiveShotCoordinate>((resolve, reject) => {
            setActiveShotCoordinate(prevValue => {
                resolve(prevValue);
                return prevValue;
            });
        })
    }

    const lastShotCoordinateAsync = () => {
        return new Promise<ActiveShotCoordinate>((resolve, reject) => {
            setLastShotCoordinate(prevValue => {
                resolve(prevValue);
                return prevValue;
            });
        })
    }

    const simulatorAsync = () => {
        return new Promise<ISimulator>((resolve, reject) => {
            setSimulator(prevValue => {
                resolve(prevValue);
                return prevValue;
            });
        })
    }

    const simulatorDataAsync = () => {
        return new Promise<SimulatorData>((resolve, reject) => {
            setSimulatorData(prevValue => {
                resolve(prevValue);
                return prevValue;
            });
        })
    }

    const checkTMConnectionAsync = () => {
        return new Promise<boolean>((resolve, reject) => {
            setCheckTMConnection(prevValue => {
                resolve(prevValue);
                return prevValue;
            });
        })
    }

    const wsAsync = () => {
        return new Promise<WebSocket>((resolve, reject) => {
            setWs(prevValue => {
                resolve(prevValue);
                return prevValue;
            });
        })
    }

    const tmLastPingAsync = () => {
        return new Promise<Date>((resolve, reject) => {
            setTmLastPing(prevValue => {
                resolve(prevValue);
                return prevValue;
            });
        })
    }

    const golferAsync = () => {
        return new Promise<Golfer>((resolve, reject) => {
            setGolfer(prevValue => {
                resolve(prevValue);
                return prevValue;
            });
        })
    }

    const fitterAsync = () => {
        return new Promise<Fitter>((resolve, reject) => {
            setFitter(prevValue => {
                resolve(prevValue);
                return prevValue;
            });
        })
    }

    const sampleShotCounterAsync = () => {
        return new Promise<number>((resolve, reject) => {
            setSampleShotCounter(prevValue => {
                resolve(prevValue);
                return prevValue;
            });
        })
    }

    const shotHistoryAsync = () => {
        return new Promise<ShotSelection.ShotHistory[]>((resolve, reject) => {
            setShotHistory(prevValue => {
                resolve(prevValue);
                return prevValue;
            });
        })
    }

    //for testing only, click on the red message bar at the top to hit the next shot from the TM test data
    const handleAnalyzeShot = async () => {
//        if (testMode) {
            let newSampleShotCounter = await sampleShotCounterAsync();
            setSampleShotCounter(newSampleShotCounter + 1);
            let msg = TrackMan.getSampleShot(newSampleShotCounter);
            let data = TrackMan.convertDataToSimulatorData(JSON.parse(msg) as TrackMan.Data);
            // let newGolfer = await golferAsync();
            // setGolfer(newGolfer);
            handleShotEvent(data, msg);
//        }
    }

    //add a practice shot to the shot history
    const addShotToHistory = async (loft: Loft, shotType: ShotSelection.ShotType, shotData: SimulatorData, fitter: Fitter, simulator: ISimulator) => {
        let newShotHistory = await shotHistoryAsync();
        FittingBL.addShotToHistory(loft, shotType, shotData, fitter, simulator, newShotHistory, intl);
        setShotHistory(newShotHistory);

        //update the grid
        let newShowShotsGroup = "";
        setShowNumberShotsGroup(prevShowShotsGroup => { newShowShotsGroup = prevShowShotsGroup; return prevShowShotsGroup });
        updateShotGrid(newShotHistory, newShowShotsGroup);
    }

    const deleteShotHistoryRecord = async (id: string) => {
        let newShotHistory = await shotHistoryAsync();
        newShotHistory = newShotHistory.filter(sh => sh.id !== id);
        setShotHistory(newShotHistory);
        updateShotGrid(newShotHistory, showNumberShotsGroup);
    }

    //show number of shots in the grid. either just the 'last shot' or all the shots taken so far
    //need to copy the correct number of shots from the shot history array to the grid's array
    const updateShotGrid = (shotHistory: ShotSelection.ShotHistory[], numberShotsGroup: string) => {
        if (shotHistory.length === 0 || numberShotsGroup === 'hideShots') {
            setShotsForGrid([]);
        } else {
            switch (numberShotsGroup) {
                case 'lastShot':
                    setShotsForGrid([shotHistory[0]]);
                    break;
                case 'allShots':
                    setShotsForGrid(shotHistory);
                    break;
            }
        }
    }

    const handleShowNumberShotsSelect = (event: ChangeEvent<HTMLSelectElement>): void => {
        setShowNumberShotsGroup(event.currentTarget.value);
        updateShotGrid(shotHistory, event.currentTarget.value);
    }

    const showPopup = (id: number) => {
        return showTutorial && tutorialId === id;
    }

    const handleTutorialNext = async () => {
        if (tutorialId === 7) {
            navigate('/apps/wedgefitting/whygrinds');
        }
        setTutorialId(tutorialId + 1);
    }

    const handleTutorialCancel = async () => {
        await TutorialHelper.disable();
        setShowTutorial(false);
        navigate('/apps/wedgefitting/golferinfo');
    }

    const handleBadHttpsSettings = () => {
        window.open(setupInstructions);
        //        window.open(`${process.env.PUBLIC_URL}/Vokey Wedge Fitting Application Setup Instructions.pdf`)
        setShowBadHttpsSettings(false);
    }

    const handlePersonClick = () => {
        //        setShowConsole(showConsole + 1);
    }

    const consoleLogWrite = (log: string) => {
        setConsoleLog(prevValue => {
            prevValue.push(`---------  ${(new Date()).toLocaleTimeString()}  --------`);
            prevValue.push(log);
            return prevValue;
        })
    }

    return (
        <div className='fitting-form'>
            <Box sx={{ flexGrow: 1, display: { xs: 'flex', sm: 'flex' } }} className='navbar'>
                <AppBar position="fixed" className='appbar'>
                    <Container maxWidth="lg">
                        <Toolbar>
                            <Grid container>
                                <Grid item xs={12} sm={4} sx={{ display: { xs: 'none', sm: 'flex' } }}>
                                    <Typography className='name' variant="h6" component="div" sx={{ marginTop: '10px', display: { xs: 'none', md: 'flex' } }}>
                                        <Person sx={{ width: '30px', marginRight: '5px', marginTop: '-10px', height: '2em' }} onClick={handlePersonClick}></Person>
                                        <GolferMenu golfer={golfer} cbgolferupdate={cbGolferUpdate}></GolferMenu>
                                    </Typography>

                                </Grid>
                                <HtmlTooltip
                                    title={
                                        <>
                                            <Typography color="inherit"><FormattedMessage id="quick.start.how.hitting" /></Typography>
                                            <br />
                                            <div style={{ textAlign: 'right' }}>
                                                <Button disableRipple onClick={handleTutorialCancel} className="cancel-button tutorial"><FormattedMessage id="general.close" /></Button>
                                                <Button disableRipple onClick={handleTutorialNext} className="submit-button tutorial"><FormattedMessage id="general.next" /></Button>
                                            </div>
                                        </>
                                    }
                                    open={showPopup(1)}
                                    placement={'right'}
                                    arrow={true}
                                    maxwidth={300}
                                >
                                    <Grid item xs={12} sm={4} sx={{ backgroundColor: 'red', borderRadius: '5px' }}>
                                        <HtmlTooltip
                                            title={
                                                <>
                                                    <Typography color="inherit"><FormattedMessage id="quick.start.fitting" /></Typography>
                                                    <br />
                                                    <div style={{ textAlign: 'right' }}>
                                                        <Button disableRipple onClick={handleTutorialCancel} className="cancel-button tutorial"><FormattedMessage id="general.close" /></Button>
                                                        <Button disableRipple onClick={handleTutorialNext} className="submit-button tutorial"><FormattedMessage id="general.next" /></Button>
                                                    </div>
                                                </>
                                            }
                                            open={showPopup(0)}
                                            placement={'bottom'}
                                            arrow={false}
                                            maxwidth={700}
                                        >

                                            <Typography component={'div'} onClick={handleAnalyzeShot} sx={{
                                                backgroundColor: 'red', color: 'white', marginTop: '0px', height: '56px', fontSize: '1.5rem', fontWeight: '600', paddingBottom: '5px', borderRadius: '5px',
                                                paddingTop: `${TMYards === "" ? "10px" : "0px"}`
                                            }} >
                                                {waitingForNextShot && <>
                                                    <span className="sk-wave" style={{ display: 'inline-flex', marginRight: '0px', marginTop: `${TMYards === "" ? "10px" : "20px"}` }} >
                                                        <span className="sk-wave-rect"></span>
                                                        <span className="sk-wave-rect"></span>
                                                        <span className="sk-wave-rect"></span>
                                                        <span className="sk-wave-rect"></span>
                                                        <span className="sk-wave-rect"></span>
                                                    </span>
                                                </>}
                                                {!waitingForNextShot && <>
                                                    {TMYards === '' && <>
                                                        <span style={{ display: 'inline', marginRight: '10px', minWidth: '100px', marginTop: '17px' }}>
                                                            {TMMessage}
                                                        </span>
                                                    </>
                                                    }
                                                    {TMYards !== '' && <>
                                                        <span style={{ display: 'inline', marginRight: '10px', minWidth: '100px' }}>
                                                            {TMMessage}
                                                            <div style={{ fontSize: '1rem', marginTop: '-5px' }}>{TMYards}</div>
                                                        </span>
                                                    </>
                                                    }
                                                </>}
                                            </Typography>
                                        </HtmlTooltip>
                                    </Grid>
                                </HtmlTooltip>
                                <Grid item xs={12} sm={4} container justifyContent="flex-end" sx={{ display: { xs: 'none', sm: 'flex' } }}>
                                    <Button disableRipple type="submit" variant="contained" onClick={handleBack} className='cancel-button' sx={{ width: '100px !important', marginTop: '10px', height: '38px' }}><FormattedMessage id="general.back" /></Button>
                                    <Button disableRipple type="submit" variant="contained" onClick={handleSubmit} className='submit-button' sx={{ marginTop: '10px', height: '38px', width: '200px !important' }}><FormattedMessage id="now.hitting.why" /></Button>
                                </Grid>
                            </Grid>
                        </Toolbar>
                    </Container>
                </AppBar>
            </Box>
            <Container maxWidth="lg">
                <Grid container sx={{ marginTop: '70px' }}>
                    <Grid item xs={12} sm={4} sx={{ height: '40px', display: { xs: 'none', sm: 'flex' } }}>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Typography sx={{ visibility: showNoClubMeasurement ? '' : 'hidden', backgroundColor: 'red', color: 'white', marginTop: '5px', height: '30px', fontSize: '1.25rem', fontWeight: '600', paddingTop: '0px', paddingBottom: '5px', borderRadius: '5px' }}>
                            <span style={{ display: 'inline', marginRight: '10px', minWidth: '100px' }}>
                                <FormattedMessage id="now.hitting.no.club.measurement" />
                            </span>
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4} container justifyContent="flex-end" sx={{ display: { xs: 'none', sm: 'flex' } }}>
                    </Grid>
                </Grid>
            </Container>
            <Container maxWidth='lg' sx={{ marginTop: '0px' }} className='your-shots'>
            </Container>
            {showConsole > 3 && (
                <Container maxWidth='lg' sx={{ marginTop: '0px' }} className='your-shots'>
                    <hr />
                    {consoleLog.map((log, logIx) => { return (<div style={{ textAlign: 'left' }} key={logIx}>{log}</div>) })}
                    <hr />
                </Container>
            )}
            <Container maxWidth='lg' sx={{ marginTop: '0px' }} className='your-shots'>
                <Grid container spacing={2} className="aaaaa">


                    <Grid item md={3} xs={12} sx={{ textAlign: 'left' }}>
                        <Grid item className='page-title' sx={{ marginLeft: '0px' }}>
                            <span><FormattedMessage id="now.hitting.shots" /></span>
                        </Grid>
                        <div>
                            <HtmlTooltip
                                title={
                                    <>
                                        <Typography color="inherit"><FormattedMessage id="quick.start.your.shots" /></Typography>
                                        <br />
                                        <div style={{ textAlign: 'right' }}>
                                            <Button disableRipple onClick={handleTutorialCancel} className="cancel-button tutorial"><FormattedMessage id="general.close" /></Button>
                                            <Button disableRipple onClick={handleTutorialNext} className="submit-button tutorial"><FormattedMessage id="general.next" /></Button>
                                        </div>
                                    </>
                                }
                                open={showPopup(2)}
                                placement={'bottom'}
                                arrow={true}
                                maxwidth={400}
                            >
                                <div>

                                    <HtmlTooltip
                                        title={
                                            <>
                                                <Typography color="inherit"><FormattedMessage id="quick.start.dots" /></Typography>
                                                <br />
                                                <div style={{ textAlign: 'right' }}>
                                                    <Button disableRipple onClick={handleTutorialCancel} className="cancel-button tutorial"><FormattedMessage id="general.close" /></Button>
                                                    <Button disableRipple onClick={handleTutorialNext} className="submit-button tutorial"><FormattedMessage id="general.next" /></Button>
                                                </div>
                                            </>
                                        }
                                        open={showPopup(3)}
                                        placement={'right-start'}
                                        arrow={true}
                                        maxwidth={400}
                                    >
                                        <div>
                                            <HtmlTooltip
                                                title={
                                                    <>
                                                        <Typography color="inherit"><FormattedMessage id="quick.start.select.shot" /></Typography>
                                                        <br />
                                                        <div style={{ textAlign: 'right' }}>
                                                            <Button disableRipple onClick={handleTutorialCancel} className="cancel-button tutorial"><FormattedMessage id="general.close" /></Button>
                                                            <Button disableRipple onClick={handleTutorialNext} className="submit-button tutorial"><FormattedMessage id="general.next" /></Button>
                                                        </div>
                                                    </>
                                                }
                                                open={showPopup(4)}
                                                placement={'right-start'}
                                                arrow={true}
                                                maxwidth={400}
                                            >
                                                <div>
                                                    <FittingYourShots golfer={golfer} fitter={fitter} loftsForFitting={golfer.loftsForFitting} activeShotCoordinate={activeShotCoordinate} lastShotCoordinate={lastShotCoordinate} handleDeleteLastShot={handleDeleteLastShot} handleSelectNewShotRow={handleSelectNewShotRow} intl={intl}></FittingYourShots>
                                                </div>
                                            </HtmlTooltip>
                                        </div>
                                    </HtmlTooltip>
                                </div>
                            </HtmlTooltip>
                        </div>
                    </Grid>


                    <Grid item md={6} xs={12}>
                        <Grid container>
                            <Grid item xs={2} sx={{ display: { xs: 'none', sm: 'flex' } }}>
                            </Grid>
                            <Grid item xs={10} className='page-title'>
                                <span><FormattedMessage id="now.hitting.technique" /></span>
                            </Grid>
                            <Grid item xs={2} sx={{ display: { xs: 'none', sm: 'flex' }, paddingLeft: '15px' }}><FittingCanvasAttackAngle width="75" height="350" intl={intl} /></Grid>
                            <HtmlTooltip
                                title={
                                    <>
                                        <Typography color="inherit"><FormattedMessage id="quick.start.your.technique" /></Typography>
                                        <br />
                                        <div style={{ textAlign: 'right' }}>
                                            <Button disableRipple onClick={handleTutorialCancel} className="cancel-button tutorial"><FormattedMessage id="general.close" /></Button>
                                            <Button disableRipple onClick={handleTutorialNext} className="submit-button tutorial"><FormattedMessage id="general.next" /></Button>
                                        </div>
                                    </>
                                }
                                open={showPopup(5)}
                                placement={'bottom'}
                                arrow={true}
                                maxwidth={400}
                            >

                                <Grid item xs={10} sx={{ textAlign: 'left' }}>
                                    <FittingCanvas id="myCanvas" width="400" height="350" style={{ borderRadius: "5px" }} shotstoplot={shotsToPlot} intl={intl} />
                                </Grid>
                            </HtmlTooltip>
                            <Grid item xs={2} sx={{ display: { xs: 'none', sm: 'flex' } }}></Grid>
                            <Grid item xs={10} sx={{ display: { xs: 'none', sm: 'flex' } }}>
                                <FittingCanvasShaftLean width="400" height="25" intl={intl} shaftleanstatus={shaftLeanStatus} />
                            </Grid>
                        </Grid>
                    </Grid>

                    <HtmlTooltip
                        title={
                            <>
                                <Typography color="inherit"><FormattedMessage id="quick.start.your.grinds" /></Typography>
                                <br />
                                <div style={{ textAlign: 'right' }}>
                                    <Button disableRipple onClick={handleTutorialCancel} className="cancel-button tutorial"><FormattedMessage id="general.close" /></Button>
                                    <Button disableRipple onClick={handleTutorialNext} className="submit-button tutorial"><FormattedMessage id="general.next" /></Button>
                                </div>
                            </>
                        }
                        open={showPopup(6)}
                        placement={'left'}
                        arrow={true}
                        maxwidth={400}
                    >

                        <Grid item md={3} sm={12} sx={{ marginLeft: { md: '0px' } }}>
                            <Grid item md={12} className='page-title' sx={{ marginLeft: '0px' }}>
                                <span><FormattedMessage id="now.hitting.grinds" /></span>
                            </Grid>


                            <HtmlTooltip
                                title={
                                    <>
                                        <Typography color="inherit"><FormattedMessage id="quick.start.full.carry" /></Typography>
                                        <br />
                                        <div style={{ textAlign: 'right' }}>
                                            <Button disableRipple onClick={handleTutorialCancel} className="cancel-button tutorial"><FormattedMessage id="general.close" /></Button>
                                            <Button disableRipple onClick={handleTutorialNext} className="submit-button tutorial"><FormattedMessage id="general.next" /></Button>
                                        </div>
                                    </>
                                }
                                open={showPopup(7)}
                                placement={'left-start'}
                                arrow={false}
                                maxwidth={400}
                            >
                                <div>
                                    <FittingYourGrinds showGapping={showGapping} distanceUnits={fitter.distanceUnits} grindRecommendations={grindRecommendations || []} fitter={fitter}></FittingYourGrinds>
                                </div>
                            </HtmlTooltip>
                        </Grid>
                    </HtmlTooltip>
                    <Grid item xs={2}>
                        <FormControl fullWidth>
                            <NativeSelect sx={{ width: '175px', display: { xs: 'none', md: 'inherit' }, marginBottom: '25px' }}
                                onChange={handleShowNumberShotsSelect}
                                value={showNumberShotsGroup}
                                inputProps={{
                                    name: 'age',
                                    id: 'uncontrolled-native',
                                }}
                            >
                                <option value='lastShot'>{intlLastShot}</option>
                                <option value='allShots'>{intlAllShots}</option>
                                <option value='hideShots'>{intlHideShots}</option>
                            </NativeSelect>
                        </FormControl>
                    </Grid>
                </Grid>


            </Container>

            <FittingShotHistory rows={shotsForGrid} callback={deleteShotCallBack} intl={intl}></FittingShotHistory>


            <Dialog
                open={showBadHttpsSettings}
                onClose={handleBadHttpsSettings}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                className="fitting-form-dialog"
            >
                <DialogContent>
                    <DialogContentText id="alert-dialog-description" sx={{ color: 'black' }}>
                        <p style={{ color: 'red' }}><FormattedMessage id="settings.connection.blocked" /></p>
                        <span><FormattedMessage id="settings.connection.blocked.info" /> </span><span style={{ fontStyle: "underline" }} onClick={handleBadHttpsSettings}><u><FormattedMessage id="settings.connection.setup.instructions" /></u></span>
                    </DialogContentText>
                </DialogContent>
            </Dialog>

            <Dialog
                open={grindDialogOpen}
                onClose={handleGrindDialogClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                className="fitting-form-dialog"
            >
                <DialogContent>
                    <DialogContentText id="alert-dialog-description" sx={{ color: 'black' }}>
                        <FormattedMessage id="now.hitting.change.club" /><span>: </span><b>{intlShotName(currentShotType)}</b>
                    </DialogContentText>
                </DialogContent>
                <DialogActions className='appbar'>
                    <Button disableRipple variant='outlined' className='submit-button' onClick={handleGrindDialogClose} sx={{ color: 'black', borderColor: 'black' }} ><FormattedMessage id="general.continue" /></Button>
                </DialogActions>
            </Dialog>


            <Dialog
                open={grindDialogOpen}
                onClose={handleGrindDialogClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                className="fitting-form-dialog"
            >
                <DialogContent>
                    <DialogContentText id="alert-dialog-description" sx={{ color: 'black' }}>
                        <FormattedMessage id="now.hitting.change.club" /><span>: </span><b><br />{intlShotName(currentShotType)} {TMYards}</b>
                    </DialogContentText>
                </DialogContent>
                <DialogActions className='appbar'>
                    <Button disableRipple variant='outlined' className='submit-button' onClick={handleGrindDialogClose} sx={{ color: 'black', borderColor: 'black' }} ><FormattedMessage id="general.continue" /></Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={rangeDialogOpen}
                onClose={handleRangeDialogClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                className="fitting-form-dialog"
            >
                <DialogContent>
                    <DialogContentText id="alert-dialog-description" sx={{ color: 'black' }}>
                        That shot was out of the recommended range. <br />You may want to delete your last shot and retake it.
                    </DialogContentText>
                </DialogContent>
                <DialogActions className='appbar'>
                    <Button disableRipple variant='outlined' className='submit-button' onClick={handleRangeDialogClose} sx={{ color: 'black', borderColor: 'black' }} ><FormattedMessage id="general.continue" /></Button>
                </DialogActions>
            </Dialog>

        </div>
    );
};
